/**
 * A database page: views as tabs (table, board, list), filters and sorts per
 * view, and editing of properties and values in place.
 */
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, Popover } from 'radix-ui';
import {
  ArrowDown,
  ArrowDownUp,
  ArrowUp,
  ChevronDown,
  FileText,
  LayoutTemplate,
  Pencil,
  Columns3,
  Filter as FilterIcon,
  MoreHorizontal,
  Plus,
  X,
} from 'lucide-react';
import {
  addView,
  applyView,
  deleteView,
  moveColumn,
  TITLE_ID,
  toggleColumn,
  updateView,
  viewColumns,
  type DatabaseSchema,
  type Filter,
  type FilterOp,
  type PropertyDef,
  type ViewDef,
  type ViewType,
} from '@nexus/core';
import {
  addRow,
  createTemplate,
  getSchema,
  saveSchema,
  type LoadedDatabase,
} from '@nexus/workspace';
import { useUi } from '../../state/ui';
import { CalendarView, GalleryView } from './calendar';
import { VIEW_ICON } from './icons';
import { viewIn, type Save } from './save';
import { useStore, useStoreQuery } from '../../db/context';
import { listAllTemplates } from '@nexus/workspace';
import { openUseTemplate } from '../../templates/state';
import { cn } from '../../lib/cn';
import { todayIso } from '../tasks/format';
import { BoardView, ListView, TableView } from './views';
import { CloseButton } from '../CloseButton';

const menu =
  'z-[70] flex min-w-44 flex-col rounded-xl border border-border bg-bg p-1 text-sm shadow-pop';
const menuItem =
  'flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-left outline-none hover:bg-bg-hover data-[highlighted]:bg-bg-hover';
const toolButton =
  'flex items-center gap-1 rounded-md px-2 py-1 text-xs text-fg-muted hover:bg-bg-hover hover:text-fg';
const control = 'rounded-md border border-border bg-bg px-1.5 py-0.5 text-xs';

const VIEW_TYPES: ViewType[] = ['table', 'board', 'list', 'calendar', 'gallery'];

/** Operators that make sense per property type. */
function opsFor(def: PropertyDef | undefined): FilterOp[] {
  switch (def?.type) {
    case undefined:
    case 'text':
    case 'url':
    case 'email':
    case 'phone':
      return ['contains', 'not_contains', 'is', 'is_not', 'empty', 'not_empty'];
    case 'number':
      return ['is', 'gt', 'lt', 'empty', 'not_empty'];
    case 'date':
      return ['on_or_after', 'on_or_before', 'after', 'before', 'empty', 'not_empty'];
    case 'checkbox':
      return ['checked', 'unchecked'];
    default:
      return ['is', 'is_not', 'empty', 'not_empty'];
  }
}
const NO_VALUE: FilterOp[] = ['empty', 'not_empty', 'checked', 'unchecked'];

function FilterRow({
  schema,
  filter,
  onChange,
  onRemove,
}: {
  schema: DatabaseSchema;
  filter: Filter;
  onChange: (f: Filter) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const def = schema.properties.find((p) => p.id === filter.propId);
  const ops = opsFor(def);
  return (
    <div className="flex items-center gap-1" data-testid="db-filter">
      <select
        aria-label={t('db.filterProperty')}
        value={filter.propId}
        onChange={(e) => {
          const next = schema.properties.find((p) => p.id === e.target.value);
          onChange({ propId: e.target.value, op: opsFor(next)[0]!, value: '' });
        }}
        className={control}
      >
        <option value={TITLE_ID}>{t('db.title')}</option>
        {schema.properties.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <select
        aria-label={t('db.filterOp')}
        value={filter.op}
        onChange={(e) => onChange({ ...filter, op: e.target.value as FilterOp })}
        className={control}
      >
        {ops.map((op) => (
          <option key={op} value={op}>
            {t(`db.ops.${op}`)}
          </option>
        ))}
      </select>
      {!NO_VALUE.includes(filter.op) &&
        (def?.options ? (
          <select
            aria-label={t('db.filterValue')}
            value={filter.value ?? ''}
            onChange={(e) => onChange({ ...filter, value: e.target.value })}
            className={control}
          >
            <option value="" />
            {def.options.map((o) => (
              <option key={o.name} value={o.name}>
                {o.name}
              </option>
            ))}
          </select>
        ) : (
          <input
            aria-label={t('db.filterValue')}
            defaultValue={filter.value ?? ''}
            placeholder={def?.type === 'date' ? t('db.datePlaceholder') : ''}
            onBlur={(e) => onChange({ ...filter, value: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            className={cn(control, 'w-28')}
          />
        ))}
      <button
        type="button"
        aria-label={t('db.removeFilter')}
        onClick={onRemove}
        className="rounded p-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

function Toolbar({ schema, view, save }: { schema: DatabaseSchema; view: ViewDef; save: Save }) {
  const { t } = useTranslation();
  const setView = (patch: Partial<ViewDef>) =>
    save((s) => updateView(s, { ...viewIn(s, view), ...patch }));
  const visible = new Set(viewColumns(schema, view).map((p) => p.id));
  const selectable = schema.properties.filter((p) => p.type === 'select' || p.type === 'status');
  return (
    <div className="flex flex-wrap items-center gap-1">
      <Popover.Root>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={cn(toolButton, view.filters.length && 'text-accent-ink')}
          >
            <FilterIcon className="size-3.5" />
            {t('db.filter')}
            {view.filters.length > 0 && ` (${view.filters.length})`}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="end" sideOffset={4} className={cn(menu, 'gap-1 p-2')}>
            <div className="-mt-1 -mr-1 flex justify-end">
              <Popover.Close asChild>
                <CloseButton className="size-6" />
              </Popover.Close>
            </div>
            {view.filters.length === 0 && (
              <p className="px-1 text-xs text-fg-muted">{t('db.noFilters')}</p>
            )}
            {view.filters.map((f, i) => (
              <FilterRow
                key={i}
                schema={schema}
                filter={f}
                onChange={(next) =>
                  setView({ filters: view.filters.map((x, j) => (j === i ? next : x)) })
                }
                onRemove={() => setView({ filters: view.filters.filter((_, j) => j !== i) })}
              />
            ))}
            <button
              type="button"
              className={menuItem}
              onClick={() =>
                setView({
                  filters: [...view.filters, { propId: TITLE_ID, op: 'contains', value: '' }],
                })
              }
            >
              <Plus className="size-3.5" />
              {t('db.addFilter')}
            </button>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <Popover.Root>
        <Popover.Trigger asChild>
          <button type="button" className={cn(toolButton, view.sorts.length && 'text-accent-ink')}>
            <ArrowDownUp className="size-3.5" />
            {t('db.sort')}
            {view.sorts.length > 0 && ` (${view.sorts.length})`}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="end" sideOffset={4} className={cn(menu, 'gap-1 p-2')}>
            <div className="-mt-1 -mr-1 flex justify-end">
              <Popover.Close asChild>
                <CloseButton className="size-6" />
              </Popover.Close>
            </div>
            {view.sorts.map((s, i) => (
              <div key={i} className="flex items-center gap-1" data-testid="db-sort">
                <select
                  aria-label={t('db.sortProperty')}
                  value={s.propId}
                  onChange={(e) =>
                    setView({
                      sorts: view.sorts.map((x, j) =>
                        j === i ? { ...x, propId: e.target.value } : x,
                      ),
                    })
                  }
                  className={control}
                >
                  <option value={TITLE_ID}>{t('db.title')}</option>
                  {schema.properties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <select
                  aria-label={t('db.sortDir')}
                  value={s.dir}
                  onChange={(e) =>
                    setView({
                      sorts: view.sorts.map((x, j) =>
                        j === i ? { ...x, dir: e.target.value as 'asc' | 'desc' } : x,
                      ),
                    })
                  }
                  className={control}
                >
                  <option value="asc">{t('db.asc')}</option>
                  <option value="desc">{t('db.desc')}</option>
                </select>
                <button
                  type="button"
                  aria-label={t('db.removeSort')}
                  onClick={() => setView({ sorts: view.sorts.filter((_, j) => j !== i) })}
                  className="rounded p-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
                >
                  <X className="size-3.5" />
                </button>
              </div>
            ))}
            <button
              type="button"
              className={menuItem}
              onClick={() => setView({ sorts: [...view.sorts, { propId: TITLE_ID, dir: 'asc' }] })}
            >
              <Plus className="size-3.5" />
              {t('db.addSort')}
            </button>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <Popover.Root>
        <Popover.Trigger asChild>
          <button type="button" className={toolButton}>
            <Columns3 className="size-3.5" />
            {t('db.properties')}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="end" sideOffset={4} className={cn(menu, 'p-2')}>
            <div className="-mt-1 -mr-1 flex justify-end">
              <Popover.Close asChild>
                <CloseButton className="size-6" />
              </Popover.Close>
            </div>
            {view.type === 'board' && (
              <label className="mb-2 flex items-center justify-between gap-2 text-xs">
                {t('db.groupBy')}
                <select
                  value={view.groupBy ?? ''}
                  onChange={(e) => setView({ groupBy: e.target.value || undefined })}
                  className={control}
                >
                  <option value="" />
                  {selectable.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {view.type === 'calendar' && (
              <label className="mb-2 flex items-center justify-between gap-2 text-xs">
                {t('db.dateBy')}
                <select
                  value={view.dateBy ?? schema.properties.find((p) => p.type === 'date')?.id ?? ''}
                  onChange={(e) => setView({ dateBy: e.target.value || undefined })}
                  className={control}
                >
                  {schema.properties
                    .filter((p) => p.type === 'date')
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                </select>
              </label>
            )}
            {/* Visible properties in view order (movable), then the hidden ones. */}
            {[
              ...viewColumns(schema, view),
              ...schema.properties.filter((p) => !visible.has(p.id)),
            ].map((p, i, all) => (
              <div key={p.id} className={cn(menuItem, 'gap-1')} data-testid="db-column">
                <label className="flex flex-1 cursor-pointer items-center justify-between gap-2">
                  <span>{p.name}</span>
                  <input
                    type="checkbox"
                    checked={visible.has(p.id)}
                    onChange={(e) => {
                      // Read it now: the save runs later, when React has reset the box.
                      const show = e.target.checked;
                      save((s) => toggleColumn(s, viewIn(s, view), p.id, show));
                    }}
                    className="accent-[var(--accent)]"
                  />
                </label>
                {visible.has(p.id) && (
                  <>
                    <button
                      type="button"
                      aria-label={t('db.moveUp', { name: p.name })}
                      disabled={i === 0}
                      onClick={() => save((s) => moveColumn(s, viewIn(s, view), p.id, -1))}
                      className="rounded p-0.5 text-fg-muted hover:bg-bg-active disabled:opacity-30"
                    >
                      <ArrowUp className="size-3" />
                    </button>
                    <button
                      type="button"
                      aria-label={t('db.moveDown', { name: p.name })}
                      disabled={!all[i + 1] || !visible.has(all[i + 1]!.id)}
                      onClick={() => save((s) => moveColumn(s, viewIn(s, view), p.id, 1))}
                      className="rounded p-0.5 text-fg-muted hover:bg-bg-active disabled:opacity-30"
                    >
                      <ArrowDown className="size-3" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
}

function ViewTabs({
  schema,
  active,
  onSelect,
  save,
  readOnly,
}: {
  schema: DatabaseSchema;
  active: ViewDef;
  onSelect: (id: string) => void;
  save: Save;
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-0.5">
      {/* Only tabs inside the tablist; the view menu and "+" sit next to it. */}
      <div role="tablist" aria-label={t('db.views')} className="flex flex-wrap items-center">
        {schema.views.map((v) => {
          const Icon = VIEW_ICON[v.type];
          const selected = v.id === active.id;
          return (
            <button
              key={v.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => onSelect(v.id)}
              onDoubleClick={() => !readOnly && setRenaming(true)}
              className={cn(
                'flex items-center gap-1.5 border-b-2 px-2 py-1 text-sm',
                selected
                  ? 'border-accent font-medium text-fg'
                  : 'border-transparent text-fg-muted hover:text-fg',
              )}
            >
              <Icon className="size-3.5" />
              {v.name}
            </button>
          );
        })}
      </div>
      {!readOnly &&
        (renaming ? (
          <input
            autoFocus
            aria-label={t('db.renameView')}
            defaultValue={active.name}
            onBlur={(e) => {
              setRenaming(false);
              const name = e.target.value.trim();
              if (name && name !== active.name)
                save((s) => updateView(s, { ...viewIn(s, active), name }));
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') setRenaming(false);
            }}
            className={cn(control, 'w-28 text-sm')}
          />
        ) : (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button
                type="button"
                aria-label={t('db.viewMenu', { name: active.name })}
                className="rounded p-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg"
              >
                <MoreHorizontal className="size-3.5" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content align="start" sideOffset={4} className={menu}>
                <DropdownMenu.Item className={menuItem} onSelect={() => setRenaming(true)}>
                  {t('db.renameView')}
                </DropdownMenu.Item>
                {VIEW_TYPES.filter((type) => type !== active.type).map((type) => (
                  <DropdownMenu.Item
                    key={type}
                    className={menuItem}
                    onSelect={() => {
                      const group =
                        type === 'board' && !active.groupBy
                          ? schema.properties.find(
                              (p) => p.type === 'status' || p.type === 'select',
                            )?.id
                          : active.groupBy;
                      save((s) => updateView(s, { ...viewIn(s, active), type, groupBy: group }));
                    }}
                  >
                    {t('db.showAs', { type: t(`db.viewTypes.${type}`) })}
                  </DropdownMenu.Item>
                ))}
                {schema.views.length > 1 && (
                  <DropdownMenu.Item
                    className={cn(menuItem, 'text-danger')}
                    onSelect={() => {
                      save((s) => deleteView(s, active.id));
                      onSelect(schema.views.find((x) => x.id !== active.id)!.id);
                    }}
                  >
                    {t('db.deleteView')}
                  </DropdownMenu.Item>
                )}
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        ))}
      {!readOnly && (
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label={t('db.addView')}
              className="ml-1 rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-fg"
            >
              <Plus className="size-3.5" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content align="start" sideOffset={4} className={menu}>
              {VIEW_TYPES.map((type) => {
                const Icon = VIEW_ICON[type];
                return (
                  <DropdownMenu.Item
                    key={type}
                    className={menuItem}
                    onSelect={() => {
                      const { view: created } = addView(schema, t(`db.viewTypes.${type}`), type);
                      save((s) => ({ ...s, views: [...s.views, created] }));
                      onSelect(created.id);
                    }}
                  >
                    <Icon className="size-3.5" />
                    {t(`db.viewTypes.${type}`)}
                  </DropdownMenu.Item>
                );
              })}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      )}
    </div>
  );
}

/** Remembered per database while the app runs. */
const activeViews = new Map<string, string>();

export function DatabaseView({
  pageId,
  db,
  embedded,
  readOnly,
}: {
  pageId: string;
  db: LoadedDatabase;
  /** Shown inside another page (dashboard): one fixed view, no tabs or toolbar. */
  embedded?: { viewId?: string; title: string };
  /** A read-only workspace membership: no editing, adding or reordering rows. */
  readOnly?: boolean;
}) {
  const { t } = useTranslation();
  const store = useStore();
  const open = useUi((s) => s.open);
  const { schema, rows } = db;
  const general =
    useStoreQuery(
      async (s) =>
        (await listAllTemplates(s)).filter((x) => !x.database && x.config.targetId === pageId),
      [pageId],
      ['pages', 'props'],
    ) ?? [];
  const [activeId, setActiveId] = useState(() => activeViews.get(pageId));
  const view =
    schema.views.find((v) => v.id === (embedded ? embedded.viewId : activeId)) ?? schema.views[0]!;
  const [justAdded, setJustAdded] = useState<string | null>(null);
  // Changes are applied one after another to the latest stored schema.
  const queue = useRef<Promise<void>>(Promise.resolve());
  const save: Save = (change) => {
    queue.current = queue.current
      .then(async () => {
        const latest = (await getSchema(store, pageId)) ?? schema;
        await saveSchema(store, pageId, typeof change === 'function' ? change(latest) : change);
      })
      .catch((e: unknown) => console.error('database schema', e));
  };
  const select = (id: string) => {
    activeViews.set(pageId, id);
    setActiveId(id);
  };
  const shown = applyView(schema, view, rows, todayIso());
  const add = async (values: Record<string, unknown> = {}, templateId?: string) => {
    const row = await addRow(store, pageId, schema, { values, templateId });
    // From a template the row is a full page: open it, like Notion.
    if (templateId) open({ kind: 'page', pageId: row.id });
    else setJustAdded(row.id);
  };
  const props = {
    databaseId: pageId,
    schema,
    view,
    rows: shown,
    save,
    add: (values?: Record<string, unknown>) => add(values),
    justAdded,
    related: db.related,
    readOnly,
  };

  // Embedded (dashboard) the block around it is the labelled region.
  const Wrapper = embedded ? 'div' : 'section';
  return (
    <Wrapper
      aria-label={embedded ? undefined : t('db.database')}
      className={embedded ? '' : 'mt-6'}
      data-testid="database"
    >
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2 border-b border-border">
        {embedded ? (
          <button
            type="button"
            onClick={() => open({ kind: 'page', pageId })}
            className="py-1 text-sm font-semibold hover:underline"
          >
            {embedded.title}
            <span className="ml-2 font-normal text-fg-muted">{view.name}</span>
          </button>
        ) : (
          <ViewTabs
            schema={schema}
            active={view}
            onSelect={select}
            save={save}
            readOnly={readOnly}
          />
        )}
        <div className="flex items-center gap-1 pb-1">
          {!embedded && !readOnly && <Toolbar schema={schema} view={view} save={save} />}
          {!readOnly && (
            <div className="flex items-stretch">
              <button
                type="button"
                onClick={() => void add()}
                className="flex items-center gap-1 rounded-l-md bg-accent px-2 py-1 text-xs font-medium text-accent-fg"
              >
                <Plus className="size-3.5" />
                {t('db.newRow')}
              </button>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    type="button"
                    aria-label={t('db.templates')}
                    className="rounded-r-md border-l border-accent-fg/20 bg-accent px-1 text-accent-fg"
                  >
                    <ChevronDown className="size-3.5" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content align="end" sideOffset={4} className={menu}>
                    <DropdownMenu.Label className="px-2 py-1 text-xs text-fg-muted">
                      {t('db.templates')}
                    </DropdownMenu.Label>
                    {db.templates.map((tpl) => (
                      <DropdownMenu.Item
                        key={tpl.id}
                        className={menuItem}
                        onSelect={() => void add({}, tpl.id)}
                      >
                        <FileText className="size-3.5" />
                        {tpl.title || t('page.untitled')}
                      </DropdownMenu.Item>
                    ))}
                    {/* General templates aimed at this database (template module). */}
                    {general.map((tpl) => (
                      <DropdownMenu.Item
                        key={tpl.page.id}
                        className={menuItem}
                        onSelect={() =>
                          openUseTemplate({
                            templateId: tpl.page.id,
                            mode: 'page',
                            targetId: pageId,
                          })
                        }
                      >
                        <LayoutTemplate className="size-3.5" />
                        {tpl.page.title || t('page.untitled')}
                      </DropdownMenu.Item>
                    ))}
                    {db.templates.length === 0 && general.length === 0 && (
                      <p className="px-2 py-1 text-xs text-fg-muted">{t('db.noTemplates')}</p>
                    )}
                    <DropdownMenu.Separator className="my-1 h-px bg-border" />
                    {db.templates.map((tpl) => (
                      <DropdownMenu.Item
                        key={`edit-${tpl.id}`}
                        className={menuItem}
                        onSelect={() => open({ kind: 'page', pageId: tpl.id })}
                      >
                        <Pencil className="size-3.5" />
                        {t('db.editTemplate', { name: tpl.title || t('page.untitled') })}
                      </DropdownMenu.Item>
                    ))}
                    <DropdownMenu.Item
                      className={menuItem}
                      onSelect={async () => {
                        const tpl = await createTemplate(
                          store,
                          pageId,
                          schema,
                          t('db.newTemplateTitle'),
                        );
                        open({ kind: 'page', pageId: tpl.id });
                      }}
                    >
                      <Plus className="size-3.5" />
                      {t('db.newTemplate')}
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
          )}
        </div>
      </div>
      {view.type === 'board' ? (
        <BoardView {...props} />
      ) : view.type === 'calendar' ? (
        <CalendarView {...props} />
      ) : view.type === 'gallery' ? (
        <GalleryView {...props} />
      ) : view.type === 'list' ? (
        <ListView {...props} />
      ) : (
        <TableView {...props} />
      )}
      {!embedded && (
        <p className="mt-2 text-xs text-fg-muted">
          {t('db.count', { shown: shown.length, count: rows.length })}
        </p>
      )}
    </Wrapper>
  );
}
